import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatApiError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, MessageSquareText, Send } from "lucide-react";
import { SmsTemplateEditor } from "@/components/settings/SmsTemplateEditor";

const OPTIONS = [
  { key: "sms_sav_client_reminder", kind: "sav_client_reminder", label: "Rappel au client la veille d'une intervention SAV", hint: "Envoyé chaque jour à 18h pour les SAV planifiés le lendemain." },
  { key: "sms_sav_client_on_the_way", kind: "sav_client_on_the_way", label: "« Le technicien arrive » au client", hint: "Envoyé quand l'intervention SAV passe « En cours »." },
  { key: "sms_sav_worker_assigned", kind: "sav_worker_assigned", label: "SMS à l'ouvrier quand on lui assigne un SAV", hint: "Avec la date, l'adresse et le lien vers l'intervention." },
  { key: "sms_order_planned_client", kind: "order_planned_client", label: "SMS au client quand son chantier passe « Au planning »", hint: "Envoyé au passage du statut, manuel ou automatique (acompte payé)." },
];

export const SmsSettingsCard = ({ config, onToggle, canEdit, onSave, saving, dirty }) => {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.getSmsStatus().then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.sendTestSms();
      toast.success("SMS de test envoyé sur votre mobile");
    } catch (err) {
      toast.error(formatApiError(err, "Échec du SMS de test"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card data-testid="sms-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <MessageSquareText className="h-5 w-5 text-primary" /> SMS automatiques
          {status && (
            <Badge variant="outline" data-testid="sms-config-status"
              className={status.configured ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}>
              {status.configured ? "Twilio connecté" : "Twilio non configuré"}
            </Badge>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Les numéros viennent des fiches clients et des fiches salariés. {status && !status.configured &&
            "Tant que les clés Twilio ne sont pas ajoutées, aucun SMS ne part (les options restent enregistrées)."}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {OPTIONS.map((o) => (
          <div key={o.key} className="p-4 border rounded-md bg-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor={o.key} className="text-sm font-medium">{o.label}</Label>
                <p className="text-xs text-muted-foreground mt-1.5">{o.hint}</p>
              </div>
              <Switch id={o.key} checked={!!config?.[o.key]} onCheckedChange={(v) => onToggle(o.key, v)}
                disabled={!canEdit} data-testid={`toggle-${o.key}`} />
            </div>
            {config?.[o.key] && status && (
              <SmsTemplateEditor orgName={config?.organization_name} kind={o.kind} value={config?.[`sms_template_${o.kind}`]}
                defaultText={status?.default_templates?.[o.kind]} disabled={!canEdit}
                onChange={(v) => onToggle(`sms_template_${o.kind}`, v)} />
            )}
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={onSave} disabled={!canEdit || saving || !dirty} data-testid="sms-save-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Enregistrer les options SMS
        </Button>
        <Button variant="outline" size="sm" onClick={sendTest} disabled={testing || !status?.configured || !canEdit}
          data-testid="sms-test-btn">
          {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
          Envoyer un SMS de test sur mon mobile
        </Button>
        </div>
      </CardContent>
    </Card>
  );
};
